import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { FiLogOut, FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { useSession } from 'src/app/hooks/useSession';
import { useNavigate, NavLink, Link } from 'react-router-dom';
import { MenuItem as MenuItemDefault } from './menu-item';
import { studentMenu } from './menu-student';
import { adminMenu } from './menu-admin';
import { MenuItemHelp } from './menu-item-help';
import { useCheckIncludeProfiles } from '@/app/hooks/useAuthorityLevel';
import { AvatarWrapper } from '@/components/ui/avatar';
import './menu.sass';

const MenuWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-menu-wrapper',
      'text-white',
      // Mobile styles (max-width: 783px)
      'max-bp-md:fixed max-bp-md:left-0 max-bp-md:right-0 max-bp-md:top-0 max-bp-md:z-[105] max-bp-md:h-[46px]',
      // Desktop styles (min-width: 784px)
      'bp-md:flex bp-md:flex-col bp-md:gap-[clamp(12px,5.5vh,44px)]',
      'bp-md:fixed bp-md:left-0 bp-md:top-0 bp-md:h-screen bp-md:w-16 bp-md:bg-primary z-[999]',
      'bp-md:!px-3 bp-md:py-[clamp(12px,3vh,24px)]',
      hasCustomLogos && 'c-menu-wrapper--has-custom-logos max-bp-md:top-[68px]', // Ajuste para logo customizada
      className
    )}
    {...props}
  />
));
MenuWrapper.displayName = 'MenuWrapper';

const MenuHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-menu-header',
      // Mobile styles
      'max-bp-md:z-[107] max-bp-md:h-[46px] max-bp-md:bg-primary-lighter max-bp-md:shadow-dropshadow',
      'max-bp-md:relative max-bp-md:flex max-bp-md:items-center max-bp-md:justify-between max-bp-md:gap-4 max-bp-md:px-4',
      // Desktop styles
      'bp-md:flex bp-md:px-0',
      className
    )}
    {...props}
  />
));
MenuHeader.displayName = 'MenuHeader';

const MenuNav = React.forwardRef(({ className, isExpanded, hasCustomLogos, ...props }, ref) => (
  <nav
    ref={ref}
    className={cn(
      'c-menu-nav',
      // Desktop
      'bp-md:flex bp-md:h-0 bp-md:w-10 bp-md:flex-grow bp-md:flex-col bp-md:justify-between',
      // Mobile - Base
      'max-bp-md:fixed max-bp-md:inset-y-[46px] max-bp-md:left-0 max-bp-md:right-0 max-bp-md:z-[106]',
      'max-bp-md:overflow-y-auto max-bp-md:bg-primary max-bp-md:p-4',
      'max-bp-md:flex max-bp-md:flex-col max-bp-md:justify-between max-bp-md:gap-4',
      'max-bp-md:translate-y-[-100%] max-bp-md:transform',
      'max-bp-md:transition-transform max-bp-md:duration-200 max-bp-md:ease-out',
      // Mobile - Offset para Custom Logos (Ajustando a si mesmo)
      hasCustomLogos &&
        cn(
          // O nav começa abaixo do header (68px do logo customizado + 46px do header do menu)
          'max-bp-md:top-[calc(68px+46px)]',
          // A altura é ajustada de acordo com o novo 'top'
          'max-bp-md:h-[calc(100dvh-68px-46px)]'
        ),
      // Mobile - Expanded State
      isExpanded && 'max-bp-md:translate-y-0',
      className
    )}
    {...props}
  />
));
MenuNav.displayName = 'MenuNav';

const MenuFavicon = React.forwardRef(({ className, src, ...props }, ref) => (
  <img
    ref={ref}
    src={src}
    alt="Logotipo"
    className={cn(
      'c-menu-favicon',
      // Mobile size
      'max-bp-md:h-8 max-bp-md:w-8',
      // Desktop size
      'bp-md:mx-auto bp-md:h-7 bp-md:w-7',
      'cursor-pointer',
      className
    )}
    {...props}
  />
));
MenuFavicon.displayName = 'MenuFavicon';

const MenuToggleButton = React.forwardRef(({ className, isExpanded, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'c-menu-toggle-btn',
      // Shared button reset
      'cursor-pointer appearance-none p-0',
      'inline-flex items-center justify-center',
      'h-9 w-9', // 36px
      'rounded-full',
      'bg-transparent',
      'transition-colors duration-150',
      // Mobile styles
      'max-bp-md:text-primary-dark',
      'max-bp-md:hover:bg-primary-lightest max-bp-md:focus:bg-primary-lightest',
      // Desktop styles (hidden)
      'bp-md:hidden',
      className
    )}
    {...props}
  >
    {/* Renderiza o ícone apropriado com o tamanho correto */}
    {React.createElement(isExpanded ? FiX : FiMenu, { className: 'w-5 h-5' })}
  </button>
));
MenuToggleButton.displayName = 'MenuToggleButton';

const MenuList = React.forwardRef(({ className, isNoGap, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-menu-list',
      'm-0 list-none p-0',
      'flex flex-col',
      // Gap condicional
      isNoGap ? 'gap-0' : 'gap-[clamp(0px,2vh,16px)]',
      className
    )}
    {...props}
  />
));
MenuList.displayName = 'MenuList';

const MenuItem = React.forwardRef(({ className, hasSubmenu, isExpanded, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'c-menu-item menu__item',
      'group/item',
      // Variants
      hasSubmenu && 'menu__item--has-submenu group/has-submenu has-submenu',
      isExpanded &&
        cn(
          'group/is-expanded is-expanded',
          // Mobile styles when expanded
          'max-bp-md:text-primary',
          'max-bp-md:bg-white',
          'max-bp-md:rounded-[20px]'
        ),
      className
    )}
    {...props}
  />
));
MenuItem.displayName = 'MenuItem';

const MenuLink = React.forwardRef(({ className, isAvatar, isActive, to, onClick, ...props }, ref) => {
  const Comp = to ? NavLink : Slot;

  return (
    <Comp
      ref={ref}
      to={to}
      onClick={onClick}
      className={cn(
        'menu__link',
        // Estado Ativo Padrão
        'aria-[current=page]:bg-primary-light',
        // ANULAÇÃO DO ESTILO ATIVO QUANDO O ITEM PAI ESTÁ EXPANDIDO (Mobile)
        // Usa o seletor arbitrário no link para anular o background ativo quando o ancestral tem a classe 'is-expanded'
        'max-bp-md:group-[.is-expanded]:aria-[current=page]:bg-transparent',
        // Variants
        isAvatar && 'menu__link--avatar',
        isActive && 'menu__link--is-active',
        className
      )}
      {...props}
    />
  );
});
MenuLink.displayName = 'MenuLink';

const MenuIcon = React.forwardRef(({ className, ...props }, ref) => (
  <Slot
    ref={ref}
    className={cn(
      'c-menu-icon',
      'w-4', // 16px
      'flex-shrink-0',
      // stroke-width: 2 é mantido pelo ícone SVG/componente (e.g., FiLogOut)
      className
    )}
    {...props}
  />
));
MenuIcon.displayName = 'MenuIcon';

const MenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-menu-label',
      // Desktop styles
      'bp-md:text-sm bp-md:font-light bp-md:opacity-0',
      'bp-md:group-hover/item:opacity-100',
      'bp-md:[.has-submenu_&]:hidden',
      className
    )}
    {...props}
  />
));
MenuLabel.displayName = 'MenuLabel';

const MenuArrow = React.forwardRef(({ isExpanded, className, ...props }, ref) => (
  <FiChevronDown
    ref={ref}
    className={cn(
      'c-menu-arrow',
      'w-4 transition-transform duration-200',
      'bp-md:group/has-submenu:hidden',
      isExpanded && 'rotate-180',
      // Desktop styles
      'bp-md:hidden',
      className
    )}
    {...props}
  />
));
MenuArrow.displayName = 'MenuArrow';

const Menu = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => {
  const navigate = useNavigate();
  const { session, signOut } = useSession();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [screenHeight, setScreenHeight] = React.useState(window.innerHeight);

  const appName = process.env.REACT_APP_NAME;
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });

  let favicon;

  switch (appName) {
    case 'unisescoop':
    case 'demo':
    case 'encceja':
    case 'siape':
    case 'canaleducacao':
    case 'egepi':
    case 'vegalabs':
    case 'inkluziva':
    case 'seducpi':
    case 'maiseducacao':
    case 'tjpara':
    case 'pit':
    case 'caboverde':
    case 'oros':
    case 'sett-solutions':
    case 'aprovavp':
    case 'floriano-educa-7':
      favicon = `/themes/${appName}/favicon/favicon-menu-32x32.png`;
      break;

    default:
      favicon = '/themes/demo/favicon/favicon-menu-32x32.png';
      throw new Error('No REACT_APP_NAME set.');
  }

  const menu = isStudent ? studentMenu() : adminMenu();

  React.useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <MenuWrapper
      ref={ref}
      hasCustomLogos={hasCustomLogos}
      className={className}
      {...props}
    >
      <MenuHeader>
        <MenuFavicon
          src={favicon}
          onClick={() => {
            navigate('/');
            setIsExpanded(false);
          }}
        />
        <MenuToggleButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </MenuHeader>

      <MenuNav
        isExpanded={isExpanded}
        hasCustomLogos={hasCustomLogos}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <MenuList isNoGap={screenHeight < 625}>
          {menu.map(item => (
            <MenuItemDefault
              item={item}
              key={item.id}
            />
          ))}
        </MenuList>

        <MenuList isNoGap={screenHeight < 625}>
          <MenuItem>
            <MenuLink
              isAvatar
              to="/profile"
            >
              <AvatarWrapper
                src={session?.user?.image?.url}
                text={session?.user?.name}
                className="[&>span]:cursor-pointer"
              />
              <MenuLabel>{session?.user?.name}</MenuLabel>
            </MenuLink>
          </MenuItem>
          <MenuItemHelp />
          <MenuItem>
            <MenuLink>
              <a onClick={() => signOut()}>
                <MenuIcon>
                  <FiLogOut />
                </MenuIcon>{' '}
                <MenuLabel>Sair</MenuLabel>
              </a>
            </MenuLink>
          </MenuItem>
        </MenuList>
      </MenuNav>
    </MenuWrapper>
  );
});

export { Menu, MenuIcon, MenuList, MenuItem, MenuLink, MenuNav, MenuHeader, MenuWrapper, MenuFavicon, MenuToggleButton, MenuLabel, MenuArrow };
